/**
 * Leitor de tela no modo discreto, central (r9 visual B2 e r9 spec B5): todo texto com a marca de oculto ("R$ •••••",
 * "g•••@d•••.com", "Total R$ ••••• em 3 parcelas") é anunciado com "valor oculto", em qualquer tela, sem cada componente
 * precisar de `anunciarOcultos`.
 *
 * Como: com o modo ligado, um MutationObserver acha os textos com a marca e dá ao elemento que os contém um nome acessível
 * falado ("R$ valor oculto"). O DOM do React nunca é movido nem trocado (só atributos que o React não controla):
 *  - link, botão e título (h1 a h6) recebem `aria-label` com o texto inteiro falado;
 *  - elemento genérico (span, p, div, strong...) só com texto e marcação sem semântica dentro vira `role="img"` com
 *    `aria-label`, o padrão para "conteúdo que se lê como um todo";
 *  - célula, item de lista, rótulo, campo e código ficam de fora: nesses casos o componente usa `anunciarOcultos`.
 * Os atributos levam `data-oculto-auto` e saem quando o texto deixa de ter a marca ou o modo desliga.
 * A prova (`scripts/provas/modo-discreto.ts`, parte `leitor`) conta os textos com marca sem nome falado em todas as telas.
 */
import i18n from 'i18next';
import { assinarDiscreto, discretoAtivo } from '../nucleo/discreto';
import { temMarcaOculto, textoFalado } from './sensivel';

const AUTO = 'data-oculto-auto';
const NOMEAVEIS = new Set(['A', 'BUTTON', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
const GENERICOS = new Set(['SPAN', 'P', 'DIV', 'STRONG', 'B', 'EM', 'I', 'SMALL', 'TIME', 'DFN', 'ABBR', 'MARK', 'S', 'U', 'SUB', 'SUP']);
/** Onde a marca já é tratada (Sensivel, anunciarOcultos), é escondida, é valor de campo ou é código. */
const FORA = '[data-oculto], [aria-hidden="true"], input, textarea, select, option, pre, code, svg, script, style, template, [contenteditable="true"]';
/** Dentro do elemento que vira imagem não pode haver nada com papel próprio (foco, lista, tabela, título). */
const COM_SEMANTICA = 'a, button, input, select, textarea, [tabindex], [role], h1, h2, h3, h4, h5, h6, ul, ol, dl, table, img, label, [contenteditable="true"]';

const marcados = new Set<HTMLElement>();

/*
 * `title` com a marca (r10 visual B11): vira a descrição acessível e o leitor soletrava "q ponto ponto ponto arroba". Com o modo
 * ligado, o tooltip passa a dizer o texto falado ("q valor oculto @r valor oculto .local"); o original volta ao desligar.
 */
const TITULO = 'data-oculto-titulo';
const titulosOriginais = new Map<Element, { original: string; falado: string }>();

function examinarTitulo(el: Element) {
  const t = el.getAttribute('title');
  if (!t || !t.includes('•') || !temMarcaOculto(t)) return;
  const falado = textoFalado(t);
  titulosOriginais.set(el, { original: t, falado });
  el.setAttribute(TITULO, '');
  el.setAttribute('title', falado);
}

function examinarTitulos(raiz: Node) {
  if (raiz.nodeType !== Node.ELEMENT_NODE && raiz.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;
  if (raiz.nodeType === Node.ELEMENT_NODE) examinarTitulo(raiz as Element);
  for (const el of (raiz as Element).querySelectorAll('[title*="•"]')) examinarTitulo(el);
}

/** Idioma trocado ao vivo (r11 visual B2): o `title` falado e os nomes acessíveis são refeitos no idioma novo. */
function retraduzir() {
  if (!observador) return;
  for (const [el, reg] of titulosOriginais) {
    if (!el.isConnected || el.getAttribute('title') !== reg.falado) continue;
    reg.falado = textoFalado(reg.original);
    el.setAttribute('title', reg.falado);
  }
  revalidar();
}

function restaurarTitulos() {
  for (const [el, { original, falado }] of titulosOriginais) {
    if (el.isConnected && el.getAttribute('title') === falado) el.setAttribute('title', original);
    el.removeAttribute(TITULO);
  }
  titulosOriginais.clear();
}
let observador: MutationObserver | null = null;

function limpar(el: HTMLElement) {
  if (el.getAttribute(AUTO) === 'img') el.removeAttribute('role');
  el.removeAttribute('aria-label');
  el.removeAttribute(AUTO);
  marcados.delete(el);
}

/** Elemento que deve receber o nome falado para o texto com a marca, ou null. */
function alvoDe(texto: Text): HTMLElement | null {
  const pai = texto.parentElement;
  if (!pai || pai.closest(FORA)) return null;
  // Sobe pelos genéricos sem semântica até o primeiro que sirva: o nome cobre a frase inteira, não só o pedaço com a marca.
  let el: HTMLElement | null = pai;
  while (el) {
    const tag = el.tagName;
    const meu = el.getAttribute(AUTO);
    if (NOMEAVEIS.has(tag)) {
      if (el.hasAttribute('aria-label') && !meu) return null;
      if (el.querySelector(COM_SEMANTICA)) return null;
      return el;
    }
    if (GENERICOS.has(tag)) {
      if ((el.hasAttribute('role') || el.hasAttribute('aria-label')) && !meu) return null;
      if (!el.querySelector(COM_SEMANTICA)) {
        // Sobe enquanto o pai também é genérico, sem outra semântica e com só este filho (mesma frase visual).
        const acima: HTMLElement | null = el.parentElement;
        if (acima && GENERICOS.has(acima.tagName) && acima.childElementCount === 1 && !acima.hasAttribute('role') && !acima.hasAttribute('aria-label') && acima.textContent?.trim() === el.textContent?.trim()) {
          el = acima;
          continue;
        }
        return el;
      }
      return null;
    }
    return null;
  }
  return null;
}

/** Texto que o leitor de tela leria dentro do elemento: sem as partes `aria-hidden` (a marca visível de um `Sensivel`). */
function textoLido(el: HTMLElement) {
  let r = '';
  const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  for (let n = w.nextNode(); n; n = w.nextNode()) {
    const escondido = n.parentElement?.closest('[aria-hidden="true"]');
    if (!escondido || !el.contains(escondido)) r += n.textContent ?? '';
  }
  return r;
}

function nomear(el: HTMLElement) {
  const texto = textoLido(el);
  if (!temMarcaOculto(texto)) return limpar(el);
  const falado = textoFalado(texto);
  if (el.getAttribute('aria-label') !== falado) el.setAttribute('aria-label', falado);
  if (!el.hasAttribute(AUTO)) {
    if (NOMEAVEIS.has(el.tagName)) el.setAttribute(AUTO, 'rotulo');
    else {
      el.setAttribute(AUTO, 'img');
      el.setAttribute('role', 'img');
    }
  }
  marcados.add(el);
}

function examinarTexto(t: Text) {
  if (!t.data.includes('•') || !temMarcaOculto(t.data)) return;
  const alvo = alvoDe(t);
  if (alvo) nomear(alvo);
}

function examinarArvore(raiz: Node) {
  if (raiz.nodeType === Node.TEXT_NODE) return examinarTexto(raiz as Text);
  if (raiz.nodeType !== Node.ELEMENT_NODE && raiz.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;
  if (!(raiz as Element).textContent?.includes('•')) return;
  const w = document.createTreeWalker(raiz, NodeFilter.SHOW_TEXT);
  for (let n = w.nextNode(); n; n = w.nextNode()) examinarTexto(n as Text);
}

function revalidar() {
  for (const el of [...marcados]) {
    if (!el.isConnected) marcados.delete(el);
    else if (!temMarcaOculto(textoLido(el)) || el.querySelector(COM_SEMANTICA)) limpar(el);
    else nomear(el);
  }
}

function aoMudar(ms: MutationRecord[]) {
  for (const m of ms) {
    if (m.type === 'characterData') examinarTexto(m.target as Text);
    else if (m.type === 'attributes') examinarTitulo(m.target as Element);
    else
      for (const n of m.addedNodes) {
        examinarArvore(n);
        examinarTitulos(n);
      }
  }
  for (const el of [...titulosOriginais.keys()]) if (!el.isConnected) titulosOriginais.delete(el);
  revalidar();
}

function ligar() {
  if (observador || typeof document === 'undefined' || !document.body) return;
  observador = new MutationObserver(aoMudar);
  observador.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['title'] });
  examinarArvore(document.body);
  examinarTitulos(document.body);
}

function desligar() {
  observador?.disconnect();
  observador = null;
  for (const el of [...marcados]) limpar(el);
  for (const el of document.querySelectorAll<HTMLElement>(`[${AUTO}]`)) limpar(el);
  restaurarTitulos();
}

/** Liga o anunciador enquanto o modo discreto estiver ligado (uma vez, no Shell). Devolve o cancelamento. */
export function iniciarAnunciadorOculto() {
  const aplicar = () => (discretoAtivo() ? ligar() : desligar());
  aplicar();
  const cancelar = assinarDiscreto(aplicar);
  i18n.on('languageChanged', retraduzir);
  return () => {
    cancelar();
    i18n.off('languageChanged', retraduzir);
    desligar();
  };
}

/** Só para teste: estado interno. */
export const elementosNomeados = () => marcados.size;
